import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Avatar,
  Button,
  IconButton,
  useToast,
  useColorModeValue,
  Icon,
  Tooltip,
  useDisclosure,
  Alert,
  AlertIcon,
  Spinner,
} from '@chakra-ui/react';
import {
  FiUser,
  FiTrash2,
  FiExternalLink,
  FiPlus,
} from 'react-icons/fi';
import AddDealPartyModal from './AddDealPartyModal';
import { supabase, isSupabaseConfigured, transformParticipantToCamelCase } from '../../utils/supabase';
import { DEAL_PARTY_ROLES } from '../../constants/crmConstants';
import { useCrmUsers } from '../../hooks/useCrmUsers';

// Known roles get their configured label; Arive can introduce new slugs
// (e.g. escrow-agent), which fall back to title-cased words.
const partyRoleLabel = (role) => {
  if (!role) return 'Related Party';
  const known = DEAL_PARTY_ROLES.find((r) => r.value === role);
  if (known) return known.label;
  return role
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
};

/**
 * Deal Team Manager (formerly Deal Parties)
 * 
 * Displays and manages the team on a deal:
 * - External parties (contacts): agents, title company, attorney, ...
 * - Internal members (CRM users): co-owner, CSC, LOA — adding one shares the
 *   deal with that user (deal_team grant, maintained by DB trigger)
 * 
 * Embedded in DealPage and DealRail.
 */
export default function DealPartiesManager({ 
  deal,
  onPartyAdded,
  onPartyRemoved,
  onViewContact,
}) {
  const toast = useToast();
  const { userMap: crmUserMap } = useCrmUsers();
  const [removing, setRemoving] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParties, setLoadingParties] = useState(true);
  
  const { isOpen: isAddPartyOpen, onOpen: onAddPartyOpen, onClose: onAddPartyClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBG = useColorModeValue('gray.50', 'gray.700');

  // Fetch participants from Supabase
  const fetchParticipants = useCallback(async () => {
    if (!deal?.id || !isSupabaseConfigured()) {
      setParticipants([]);
      setLoadingParties(false);
      return;
    }

    setLoadingParties(true);

    try {
      const { data, error } = await supabase
        .from('crm_deal_participants')
        .select(`
          *,
          crm_contacts!contact_id(id, first_name, last_name, email, phone, contact_types)
        `)
        .eq('deal_id', deal.id);

      if (error) {
        console.error('[DealPartiesManager] Error fetching participants:', error);
        setParticipants([]);
      } else {
        // Transform to expected format. Internal rows (user_id) resolve their
        // name from the CRM users directory; external rows from the contact.
        const transformed = (data || []).map(row => {
          const isInternal = !!row.user_id;
          const teamUser = isInternal ? crmUserMap.get(row.user_id) : null;
          return {
            id: row.id,
            contactId: row.contact_id,
            userId: row.user_id || null,
            isInternal,
            role: row.role,
            isPrimary: row.is_primary,
            source: row.source || 'manual',
            createdAt: row.created_at,
            contactName: isInternal
              ? (teamUser?.name || teamUser?.email || 'Team member')
              : (row.crm_contacts
                ? `${row.crm_contacts.first_name || ''} ${row.crm_contacts.last_name || ''}`.trim() || 'Unknown'
                : 'Unknown'),
            contactEmail: isInternal ? (teamUser?.email || '') : (row.crm_contacts?.email || ''),
            contactPhone: isInternal ? '' : (row.crm_contacts?.phone || ''),
            contactTypes: isInternal ? [] : (row.crm_contacts?.contact_types || []),
          };
        });
        setParticipants(transformed);
      }
    } catch (err) {
      console.error('[DealPartiesManager] Unexpected error:', err);
      setParticipants([]);
    } finally {
      setLoadingParties(false);
    }
  }, [deal?.id, crmUserMap]);

  useEffect(() => {
    fetchParticipants();

    // Set up real-time subscription
    if (!deal?.id || !isSupabaseConfigured()) return;

    const channel = supabase
      .channel(`deal-participants-${deal.id}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deal_participants',
          filter: `deal_id=eq.${deal.id}`,
        },
        () => {
          fetchParticipants();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [deal?.id, fetchParticipants]);

  const handleRemoveParty = async (party) => {
    const confirmMessage = party.isInternal
      ? `Remove ${party.contactName || 'this team member'} from the deal team?\n\nThis also revokes their access to this deal (unless it is shared with them another way).`
      : party.source === 'arive_sync'
        ? `Remove ${party.contactName || 'this party'} from the deal?\n\nThis party is synced from Arive — they will reappear on the next sync unless they are also removed from the loan in Arive.`
        : `Remove ${party.contactName || 'this party'} from the deal?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    if (!isSupabaseConfigured()) {
      toast({
        title: 'Database not configured',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    // Store previous state for rollback on error
    const previousParticipants = [...participants];
    
    // INSTANT UPDATE: Remove from local state immediately
    setParticipants(prev => prev.filter(p => p.id !== party.id));
    
    setRemoving(party.contactId);
    try {
      const { error } = await supabase
        .from('crm_deal_participants')
        .delete()
        .eq('id', party.id);

      if (error) throw error;

      toast({
        title: 'Party removed',
        description: `${party.contactName || 'Contact'} removed from deal`,
        status: 'success',
        duration: 2000,
      });

      if (onPartyRemoved) {
        onPartyRemoved();
      }
    } catch (error) {
      console.error('Error removing party:', error);
      // ROLLBACK: Restore previous state on error
      setParticipants(previousParticipants);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setRemoving(null);
    }
  };

  const handlePartyAdded = (result) => {
    // INSTANT UPDATE: Add to local state immediately
    if (result?.success && result?.party) {
      setParticipants(prev => [...prev, result.party]);
    }

    if (onPartyAdded) {
      onPartyAdded();
    }

    onAddPartyClose();
  };

  return (
    <Box>
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" flexWrap="wrap" gap={2} minW={0}>
          <HStack spacing={2} minW={0}>
            <Icon as={FiUser} boxSize={5} color="purple.500" flexShrink={0} />
            <Heading size="sm">Deal Team</Heading>
          </HStack>
          <Button
            size="sm"
            leftIcon={<FiPlus />}
            colorScheme="purple"
            flexShrink={0}
            onClick={onAddPartyOpen}
          >
            Add to Team
          </Button>
        </HStack>

        {loadingParties ? (
          <Box p={6} textAlign="center">
            <Spinner size="sm" />
            <Text fontSize="sm" color="gray.500" mt={2}>Loading parties...</Text>
          </Box>
        ) : participants.length === 0 ? (
          <Box p={6} bg={cardBG} borderRadius="md" textAlign="center">
            <Icon as={FiUser} boxSize={10} color="gray.300" mb={2} />
            <Text color="gray.500" fontSize="sm">
              No deal team yet
            </Text>
            <Text fontSize="xs" color="gray.400" mb={3}>
              Add internal members (co-owner, CSC) or external parties (agents, title company)
            </Text>
            <Button
              size="sm"
              leftIcon={<FiPlus />}
              colorScheme="purple"
              variant="outline"
              onClick={onAddPartyOpen}
            >
              Add First Member
            </Button>
          </Box>
        ) : (
          <VStack align="stretch" spacing={3}>
            {participants.map((party, index) => (
              <Box
                key={party.id || `${party.contactId}-${party.role}-${index}`}
                p={4}
                bg={cardBG}
                borderRadius="md"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <HStack justify="space-between" align="flex-start" gap={2} minW={0}>
                  <HStack spacing={3} flex="1 1 0" minW={0}>
                    <Avatar
                      size="sm"
                      name={party.contactName || 'Unknown'}
                      flexShrink={0}
                    />
                    <VStack align="flex-start" spacing={1} minW={0}>
                      <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                        {party.contactName || 'Unknown Contact'}
                      </Text>
                      <HStack spacing={2} flexWrap="wrap" gap={1}>
                        <Badge colorScheme="purple" fontSize="xs">
                          {partyRoleLabel(party.role)}
                        </Badge>
                        {party.isInternal && (
                          <Tooltip label="Internal team member — this deal is shared with them.">
                            <Badge colorScheme="teal" fontSize="xs" variant="subtle">Team</Badge>
                          </Tooltip>
                        )}
                        {party.isPrimary && (
                          <Badge colorScheme="blue" fontSize="xs">Primary</Badge>
                        )}
                        {party.source === 'arive_sync' && (
                          <Tooltip label="Synced automatically from Arive. If removed, it will reappear on the next sync while this party is on the loan in Arive.">
                            <Badge colorScheme="orange" fontSize="xs" variant="subtle">Arive</Badge>
                          </Tooltip>
                        )}
                      </HStack>
                      {party.contactEmail && (
                        <Text fontSize="xs" color="gray.600" wordBreak="break-all">{party.contactEmail}</Text>
                      )}
                      {party.contactPhone && (
                        <Text fontSize="xs" color="gray.600">{party.contactPhone}</Text>
                      )}
                    </VStack>
                  </HStack>

                  <HStack spacing={1} flexShrink={0}>
                    {onViewContact && !party.isInternal && (
                      <Tooltip label="View contact profile">
                        <IconButton
                          icon={<FiExternalLink />}
                          size="xs"
                          variant="ghost"
                          aria-label="View contact"
                          onClick={() => onViewContact(party.contactId)}
                        />
                      </Tooltip>
                    )}
                    <Tooltip label="Remove from deal">
                      <IconButton
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Remove party"
                        onClick={() => handleRemoveParty(party)}
                        isLoading={removing === party.contactId}
                      />
                    </Tooltip>
                  </HStack>
                </HStack>
              </Box>
            ))}
          </VStack>
        )}

        <Alert status="info" size="sm" borderRadius="md">
          <AlertIcon />
          <Text fontSize="xs">
            The deal team is specific to this deal — it won't carry over to future deals for this
            borrower. Internal members (Team badge) can see this deal in their own Origin.
          </Text>
        </Alert>
      </VStack>

      {/* Add Party Modal */}
      <AddDealPartyModal
        isOpen={isAddPartyOpen}
        onClose={onAddPartyClose}
        dealId={deal?.id}
        existingParties={participants}
        onPartyAdded={handlePartyAdded}
      />
    </Box>
  );
}
